import { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { EditableGrid } from "../../components/DataTable/EditableGrid";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchInventoryDetail,
  fetchInventorySummary,
  selectInventoryDetail,
  clearInventoryDetail,
  updateInventoryTransactions,
  selectUpdating,
  selectUpdateError,
  clearError,
} from "../../slices/inventorymanagement.slice";
import { v4 as uuidv4 } from "uuid";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { ActionCell } from "../../components/DataTable/customcell/ActionCell";
import { toast } from "react-toastify";

interface Props {
  open: boolean;
  handleClose: () => void;
  productId: number;
  productName: string;
}

const newPurchaseRow = {
  purchaseDate: new Date(),
  quantityPurchased: 0,
  unitPrice: 0,
  invoiceRef: "",
  unit: "",
};

const formatNumber = (value: any): string => {
  if (value === null || value === undefined || value === "") return "0.00";
  const num = parseFloat(value);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

export const EditInventoryModal = ({
  handleClose,
  open,
  productId,
  productName,
}: Props) => {
  const dispatch = useAppDispatch();
  const inventoryDetail = useAppSelector(selectInventoryDetail);
  const inventorySummary = useAppSelector(
    (state) => state.inventorymanagement.inventorySummary
  );
  const updating = useAppSelector(selectUpdating);
  const updateError = useAppSelector(selectUpdateError);

  const [tableData, setTableData] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});

  const selectedSummaryItem = inventorySummary.find(
    (item: any) => item.productId === productId
  );

  useEffect(() => {
    if (open && productId) {
      dispatch(fetchInventoryDetail(productId));
      dispatch(clearError());
    }
    return () => {
      dispatch(clearInventoryDetail());
    };
  }, [dispatch, open, productId]);

  useEffect(() => {
    if (inventoryDetail?.transactions) {
      const purchaseData = inventoryDetail.transactions.map(
        (transaction: any) => ({
          id: transaction.id,
          purchaseDate: moment(transaction.purchaseDate).toDate(),
          quantityPurchased: transaction.quantityPurchased || 0,
          unit: transaction.unit || "lb",
          unitPrice: transaction.unitPrice || 0,
          invoiceRef: transaction.invoiceRef || "",
        })
      );
      setTableData(purchaseData);
    }
  }, [inventoryDetail]);

  const addNewPurchase = () => {
    setTableData((prev) => [
      ...prev,
      {
        ...newPurchaseRow,
        id: uuidv4(),
        unit: selectedSummaryItem?.unit || inventoryDetail?.unit || "lb",
      },
    ]);
  };

  const onItemChange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = { ...item };
        updatedRow[field] = value;
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  useEffect(() => {
    const errors: string[] = [];
    tableData.forEach((item, index) => {
      if (!item.purchaseDate) errors.push(`purchaseDate_${index}`);
      if (!item.quantityPurchased || item.quantityPurchased <= 0)
        errors.push(`quantityPurchased_${index}`);
      if (!item.unitPrice || item.unitPrice <= 0)
        errors.push(`unitPrice_${index}`);
      if (!item.unit || item.unit.trim() === "") errors.push(`unit_${index}`);
    });
    setErrorFields(errors);

    // Set all rows to edit mode
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  const handleSave = async () => {
    if (errorFields.length > 0) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    const requestData = {
      productId: productId,
      purchases: tableData.map((item: any) => {
        const purchaseData: any = {
          quantityPurchased: parseFloat(item.quantityPurchased) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0,
          purchaseDate: moment(item.purchaseDate).format("YYYY-MM-DD"),
          invoiceRef: item.invoiceRef?.toString() || "",
          unit: item.unit?.toString() || "",
        };
        if (typeof item.id === "number" && item.id > 0) {
          purchaseData.id = item.id;
        }

        return purchaseData;
      }),
    };

    try {
      await dispatch(updateInventoryTransactions(requestData)).unwrap();

      // Refresh the inventory data after successful update
      dispatch(fetchInventorySummary());
      dispatch(fetchInventoryDetail(productId));

      toast.success("Inventory updated successfully!");
      handleClose();
    } catch (error: any) {
      console.error("Failed to update inventory:", error);
      toast.error(`Failed to update inventory: ${error}`);
    }
  };
  const deletePurchase = (dataItem: any) => {
    setTableData(tableData.filter((item: any) => item.id !== dataItem.id));
  };

  const handleModalClose = () => {
    dispatch(clearError());
    handleClose();
  };

  return (
    <BaseModal open={open} handleClose={handleModalClose}>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={3}
        sx={{
          maxHeight: "85vh",
          overflow: "hidden",
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Edit Inventory - {productName}
        </Typography>

        {/* Read-only Summary Fields */}
        <Box display={"flex"} flexWrap={"wrap"} gap={2}>
          <TextField
            label="Product Name"
            variant="outlined"
            value={productName}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ minWidth: "200px" }}
          />
          <TextField
            label="Current Stock"
            variant="outlined"
            value={formatNumber(
              selectedSummaryItem?.currentStock ||
                inventoryDetail?.currentStock ||
                0
            )}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ minWidth: "150px" }}
          />
          <TextField
            label="Total Purchased"
            variant="outlined"
            value={formatNumber(
              selectedSummaryItem?.totalPurchased ||
                inventoryDetail?.totalPurchased ||
                0
            )}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ minWidth: "150px" }}
          />
          <TextField
            label="Unit"
            variant="outlined"
            value={selectedSummaryItem?.unit || inventoryDetail?.unit || ""}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ minWidth: "100px" }}
          />
        </Box>

        {/* Display update error if any */}
        {updateError && (
          <Typography color="error" variant="body2">
            {updateError}
          </Typography>
        )}

        {/* Editable Grid Section */}
        <Box>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            mb={1}
            alignItems={"center"}
          >
            <Typography variant="subtitle1" fontWeight="600">
              Purchase History
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={addNewPurchase}
              disabled={updating}
            >
              Add Purchase
            </Button>
          </Box>

          <Box
            sx={{
              maxHeight: "200px",
              overflow: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            <EditableGrid
              dataItemKey="id"
              onItemChange={onItemChange}
              edit={edit}
              columns={[
                {
                  field: "purchaseDate",
                  title: "Purchase Date",
                  editor: "date",
                  format: "{0:d}",
                  width: 180,
                },
                {
                  field: "quantityPurchased",
                  title: "Quantity",
                  editor: "numeric",
                  width: 180,
                },
                {
                  field: "unitPrice",
                  title: "Unit Price",
                  editor: "numeric",
                  width: 170,
                },
                {
                  field: "invoiceRef",
                  title: "Invoice Reference",
                  editor: "text",
                  width: 190,
                },
                {
                  title: "Actions",
                  width: 120,
                  cells: {
                    data: (props) => (
                      <ActionCell {...props} onDelete={deletePurchase} />
                    ),
                  },
                },
              ]}
              data={tableData}
            />
          </Box>
        </Box>

        {/* Footer Buttons */}
        <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={!(tableData.length && !errorFields.length) || updating}
            onClick={handleSave}
          >
            {updating ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleModalClose}
            disabled={updating}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
